<template>
  <card :style="`--season-color: rgba(${toRGB(season.rgbColorCode)}, 0.35);`"
        :title="$t('seasonRateEditor.seasonRestrictionEditor.card.restrictions')"
        header-style-class="card__header--season-color"
        cols="12">
    <hm-card-inner>
      <hm-col cols="12">
        <hm-input-toggle v-model="internalValue.restrictions.bookable"
                         :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('Bookable'))"
                         :label="$t('seasonRateEditor.seasonRestrictionEditor.bookable')"
                         :enabled-title="$t('seasonRateEditor.seasonRestrictionEditor.bookableEnabled')"
                         :disabled-title="$t('seasonRateEditor.seasonRestrictionEditor.bookableDisabled')"
                         name="bookable" />
        <hm-input-number v-model="internalValue.restrictions.minLOS"
                         :validate="`max_value:${internalValue.restrictions.maxLOS || 365}|min_value:1`"
                         :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('MinLOS'))"
                         :label="$t('seasonRateEditor.seasonRestrictionEditor.minLOS')"
                         :help="$t('seasonRateEditor.seasonRestrictionEditor.minLOS_help')"
                         name="minLOS"/>
        <hm-input-number v-model="internalValue.restrictions.maxLOS"
                         :validate="`min_value:${internalValue.restrictions.minLOS || 1}`"
                         :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('MaxLOS'))"
                         :label="$t('seasonRateEditor.seasonRestrictionEditor.maxLOS')"
                         :help="$t('seasonRateEditor.seasonRestrictionEditor.maxLOS_help')"
                         name="maxLOS"/>
        <hm-input-number v-if="hasLicense('LeadTimeToBook')"
                         v-model="internalValue.restrictions.minLeadTimeToBook"
                         :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('MinimumLeadTimeToBook'))"
                         :label="$t('seasonRateEditor.seasonRestrictionEditor.minLeadTimeToBook')"
                         :help="$t('seasonRateEditor.seasonRestrictionEditor.minLeadTimeToBook_help')"
                         name="minLeadTimeToBook"/>
        <hm-input-number v-if="hasLicense('LeadTimeToBook')"
                         v-model="internalValue.restrictions.maxLeadTimeToBook"
                         :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('MaximumLeadTimeToBook'))"
                         :label="$t('seasonRateEditor.seasonRestrictionEditor.maxLeadTimeToBook')"
                         :help="$t('seasonRateEditor.seasonRestrictionEditor.maxLeadTimeToBook_help')"
                         name="maxLeadTimeToBook"/>
        <hm-input-number v-model="internalValue.restrictions.minStayThrough"
                         :validate="`max_value:${internalValue.restrictions.maxStayThrough || 365}|min_value:1`"
                         :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('MinStayThrough'))"
                         :label="$t('seasonRateEditor.seasonRestrictionEditor.minStayThrough')"
                         :help="$t('seasonRateEditor.seasonRestrictionEditor.minStayThrough_help')"
                         name="minStayThrough"/>
        <hm-input-number v-model="internalValue.restrictions.maxStayThrough"
                         :validate="`min_value:${internalValue.restrictions.minStayThrough || 1}`"
                         :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('MaxStayThrough'))"
                         :label="$t('seasonRateEditor.seasonRestrictionEditor.maxStayThrough')"
                         :help="$t('seasonRateEditor.seasonRestrictionEditor.maxStayThrough_help')"
                         name="maxStayThrough"/>
      </hm-col>
    </hm-card-inner>
    <hm-card-inner :title="$t('seasonRateEditor.seasonRestrictionEditor.card.arrivalDoW')">
      <hm-col :grid="true" cols="12" style="--column-count:7;--column-width:20px;" class="grid--column-count">
        <template v-for="i in 7">
          <hm-input-checkbox v-model="internalValue.restrictions.arrivalDOW[i-1]"
                             :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('ArrivalDOW'))"
                             :label="getWeekdayLabel(i - 1)"
                             :name="`arrivalDOW_${i}`"
                             :key="i"
                             :show-label="true"
                             cols="1"/>
        </template>
      </hm-col>
      <hm-col cols="12">
        <span class="hm-input__helper-text hm-checkbox__helper-text">{{ $t('seasonRateEditor.seasonRestrictionEditor.arrivalDoW_help') }}</span>
      </hm-col>
    </hm-card-inner>
    <hm-card-inner :title="$t('seasonRateEditor.seasonRestrictionEditor.card.departureDoW')">
      <hm-col :grid="true" cols="12" style="--column-count:7;--column-width:20px;" class="grid--column-count">
        <template v-for="i in 7">
          <hm-input-checkbox v-model="internalValue.restrictions.departureDOW[i-1]"
                             :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('DepartureDOW'))"
                             :label="getWeekdayLabel(i - 1)"
                             :name="`departureDOW_${i}`"
                             :key="i"
                             :show-label="true"
                             cols="1"/>
        </template>
      </hm-col>
      <hm-col cols="12">
        <span class="hm-input__helper-text hm-checkbox__helper-text">{{ $t('seasonRateEditor.seasonRestrictionEditor.departureDoW_help') }}</span>
      </hm-col>
    </hm-card-inner>
    <hm-card-inner :title="$t('seasonRateEditor.seasonRestrictionEditor.card.fplos')">
      <hm-col :grid="true" cols="12" style="--column-count:8;--column-width:20px;" class="grid--column-count">
        <template v-for="i in 8">
          <hm-input-checkbox v-model="internalValue.restrictions.fpLOS[i-1]"
                             :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('FPLOS'))"
                             :label="`${i <= 7 ? i : '+'}`"
                             :name="`fplos_${i}`"
                             :key="i"
                             :show-label="true"
                             cols="1"/>
        </template>
      </hm-col>
      <hm-col cols="12">
        <span :title="$t('seasonRateEditor.seasonRestrictionEditor.fplos_help')" class="hm-input__helper-text hm-checkbox__helper-text">{{ $t('seasonRateEditor.seasonRestrictionEditor.fplos_help') }}</span>
      </hm-col>
    </hm-card-inner>
    <hm-card-inner v-if="!isEmpty(internalValue.restrictions.guaranteePolicyType) && isEmpty(ratePlan.policyId)" :title="$t('seasonRateEditor.seasonRestrictionEditor.card.policies')">
      <hm-input-select ref="guaranteePolicyType"
                       v-model="internalValue.restrictions.guaranteePolicyType"
                       :disabled="disabled || (ratePlan.ratePlanMode === 'Derived' && !isNil(ratePlan.inheritRestrictions) && ratePlan.inheritRestrictions.includes('GuaranteePolicyType'))"
                       :label="$t('seasonRateEditor.seasonRestrictionEditor.guaranteePolicyType')"
                       :help="$t('seasonRateEditor.seasonRestrictionEditor.guaranteePolicyType_help')"
                       :no-select="true"
                       name="guaranteePolicyType"
                       enum-name="GuaranteePolicyType" />
    </hm-card-inner>
  </card>
</template>

<script>
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import SaveWatchMixin from '../../../mixins/SaveWatchMixin';
import { isEmpty } from 'lodash';
import HmCardInner from '../layout/HmCardInner';
import HmInputMoney from '../inputs/HmInputMoney';
import HmInputNumber from '../inputs/HmInputNumber';

export default {
  name: 'SeasonRestrictionEditor',
  components: { HmInputNumber, HmInputMoney, HmCardInner },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'internalValue',
      variable: 'internalValue',
      fn: function (cbSaveComplete) {
        if (this.disabled !== true) {
          this.$emit('input', this.internalValue);
          this.axios.put(this.serviceUrls.seasonRate.updateRatePlanSeasonDetail(this, this.ratePlan.id), this.internalValue)
            .then((response) => {
              cbSaveComplete();
            })
            .catch((error) => {
              this.showMessages(error.response);
              cbSaveComplete();
            });
        } else {
          cbSaveComplete();
        }
      },
    },
  ],

  props: {
    season: {
      required: true,
      type: Object,
    },
    ratePlan: {
      required: true,
      type: Object,
    },
    value: {
      required: true,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      internalValue: this.value,
    };
  },


  watch: {
    value: function (newVal) {
      this.internalValue = this.value;
    },
    internalValue: function (newVal) {
      if (this.disabled !== true) {
        this.$emit('input', this.internalValue);
      }
    },
  },

  created() {
    this.internalValue = this.value;
  },

  methods: {
    toRGB(hexColorCode) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColorCode);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '';
    },
    getWeekdayLabel(weekdayNumber) { // 0 == Monday
      weekdayNumber += 1;
      if (weekdayNumber > 6) weekdayNumber -= 7;
      return this.$moment.localeData().weekdaysMin()[weekdayNumber]; // 0 = Sunday
    },
  },
};
</script>

<style scoped lang="scss">

</style>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/lib/seasonRateEditor/SeasonRestrictionEditor.vue